import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, computed, forwardRef, inject, input, model, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormCheckboxControl, ValidationError } from '@angular/forms/signals';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { checkboxBridgeStyles, checkboxStyleId } from './checkbox.theme';

export type CheckboxSize = 'xsmall' | 'small' | 'medium' | 'large';

@Component({
  selector: 'wb-checkbox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': '$hostClass()',
  },
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxComponent), multi: true }],
  templateUrl: 'checkbox.component.html',
  styleUrls: ['checkbox.component.less'],
})
export class CheckboxComponent implements FormCheckboxControl, ControlValueAccessor {

  private readonly document = inject(DOCUMENT);

  // ── Signal Forms Contract (FormCheckboxControl) ──
  public readonly checked = model<boolean>(false);
  public readonly disabled = input(false);
  public readonly readonly = input(false);
  public readonly touched = input(false);
  public readonly touch = output<void>();
  public readonly errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);
  public readonly invalid = input(false);
  public readonly required = input(false);
  public readonly hidden = input(false);
  public readonly pending = input(false);
  public readonly dirty = input(false);
  public readonly name = input<string>('');

  // ── Component-specific inputs ──
  public $size = input<CheckboxSize>('small', { alias: 'size' });
  public $label = input<string>(null, { alias: 'label' });
  public $description = input<string>(null, { alias: 'description' });
  public $indeterminate = input<boolean>(false, { alias: 'indeterminate' });
  public $tabindex = input<number | null>(null, { alias: 'tabindex' });

  public onToggle = output<boolean>();

  public $hostClass = computed(() => {

    const size = this.$size();
    const classes: string[] = ['wb-checkbox'];
    if (size === 'xsmall') classes.push('check-xsmall');
    if (size === 'small') classes.push('check-small');
    if (size === 'medium') classes.push('check-medium');
    if (size === 'large') classes.push('check-large');
    if (this.checked()) classes.push('checked');
    if (this.$indeterminate()) classes.push('indeterminate');
    if (this.disabled()) classes.push('disabled');
    return classes.join(' ');
  });

  // ── CVA ──
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  public constructor() {

    DesignSystem.Mount(this.document, checkboxStyleId, checkboxBridgeStyles);
  }

  public toggle(): void {

    if (this.disabled() || this.readonly()) return;

    const next = !this.checked();
    this.checked.set(next);
    this._onChange(next);
    this._onTouched();
    this.touch.emit();
    this.onToggle.emit(next);
  }

  protected onBoxClick(event: Event): void {

    event.preventDefault();
    this.toggle();
  }

  protected onKeydown(event: KeyboardEvent): void {

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  public writeValue(value: boolean): void {

    this.checked.set(!!value);
  }

  public registerOnChange(fn: (value: boolean) => void): void {

    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this._onTouched = fn;
  }

  public setDisabledState(_isDisabled: boolean): void {
  }
}
